import { call, put, takeLatest, cancelled } from 'redux-saga/effects';
import {
  CLAIMS_METRICS_EXCLUSION_REQUEST,
  CLAIMS_PROCESSED_REQUEST,
  DOP_CLAIMS_METRICS_CLAIMS_REQUEST,
  DOP_CLAIMS_METRICS_TAT_REQUEST,
  CLAIMS_PROCESSED_MONTHLY_REQUEST,
} from '../../../actions/actionTypes';
import { fetchClaimsMetricsExclusionError, fetchClaimsMetricsExclusionLoading, fetchClaimsMetricsExclusionSuccess, fetchClaimsProcessedError, fetchClaimsProcessedLoading, fetchClaimsProcessedSuccess, fetchDopClaimsMetricsClaimsError, fetchDopClaimsMetricsClaimsLoading, fetchDopClaimsMetricsClaimsSuccess, fetchDopClaimsMetricsTatError, fetchDopClaimsMetricsTatLoading, fetchDopClaimsMetricsTatSuccess, fetchClaimsProcessedMonthlyError, fetchClaimsProcessedMonthlyLoading, fetchClaimsProcessedMonthlySuccess, fetchClaimsProcessedLobError, fetchClaimsProcessedLobLoading, fetchClaimsProcessedLobSuccess } from '@src/middleware/actions/Reimbursement/claimsProcessedActions';
import { getClaimsMetricsExclusionDataRequest, getClaimsProcessedDataRequest, getDopClaimsMetricsClaimsDataRequest, getDopClaimsMetricsTatDataRequest, getClaimsProcessedMonthlyDataRequest } from '@src/middleware/services/reimbusement/claimsProcessed/claimsProcessed.service';
import { get } from 'lodash';
import { startJobPolling } from '@src/utility/CommonBulkPoll';
import { reimbursementPollingStarted, reimbursementPollingCompleted } from '@src/middleware/actions/Reimbursement/reiumbursementTabDataAction';

export function* getClaimsProcessedData(action: any) {
  // Get highVolumeTinFlag from action payload (passed from ClaimsProcessed.tsx)
  // false/undefined = normal flow, true = polling flow with high volume TIN
  const highVolumeTinFlag = action?.highVolumeTinFlag;
  const correlationId = `getClaimsProcessedData-${Date.now()}`;
  try {
    yield put(fetchClaimsProcessedLoading(true));
    
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingStarted(correlationId));
    }
    
    // Step 1: Call getClaimsProcessedDataRequest with highVolumeTinFlag
    // - If highVolumeTinFlag is false/undefined: Normal flow → Standard API → Immediate response
    // - If highVolumeTinFlag is true: Polling flow → Bulk API → Job submission
    const response = yield call(
      getClaimsProcessedDataRequest,
      action?.payload,
      highVolumeTinFlag
    );
    
    // Check which flow based on highVolumeTinFlag
    if (highVolumeTinFlag === true) {
      // ============================================================
      // POLLING FLOW - Response contains jobId for polling
      // ============================================================
      
      // Step 2: Extract jobId from job submission response
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === "ACCEPTED" || 
                            response?.Data?.Message === "Job submitted successfully";
      
      if (jobId && isJobSubmitted) {
        // Step 3: Start polling with the jobId - Poll GET /job-status/result/{jobId}
        let fullPollingResponse: any = null;
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId, // Pass jobId as parameter
              {
                onInProgress: () => {},
                onCompleted: (result) => {
                  resolve(result);
                },
                onFailed: (errorMessage) => {
                  reject(new Error(errorMessage));
                },
                onStatusUpdate: (response) => {
                  // Capture the full polling response
                  fullPollingResponse = response;
                }
              },
              '/job-status/result',
              1000
            );
          });
        };
        
        // Step 4: Wait for polling to complete
        try {
          const pollingResult = yield call(pollingPromise);
          const transformedData = pollingResult || [];
          
          yield put(fetchClaimsProcessedSuccess(transformedData));
        } catch (pollingError: any) {
          yield put(fetchClaimsProcessedError(true));
        } finally {
          yield put(fetchClaimsProcessedLoading(false));
          yield put(reimbursementPollingCompleted(correlationId));
        }
      } else {
        yield put(fetchClaimsProcessedError(true));
        yield put(fetchClaimsProcessedLoading(false));
        yield put(reimbursementPollingCompleted(correlationId));
      }
    } else {
      // ============================================================
      // NORMAL FLOW - Response contains actual data (no disturbance)
      // ============================================================
      if (response) {
        yield put(fetchClaimsProcessedSuccess(response));
      } else {
        yield put(fetchClaimsProcessedError(true));
      }
      yield put(fetchClaimsProcessedLoading(false));
    }
  } catch (error: any) {
    yield put(fetchClaimsProcessedError(true));
    yield put(fetchClaimsProcessedLoading(false));
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      yield put(fetchClaimsProcessedLoading(false));
      if (highVolumeTinFlag === true) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* getClaimsMetricsExclusionData(action: any) {
  // Support dual flow via highVolumeTinFlag
  const highVolumeTinFlag = action?.highVolumeTinFlag;
  const correlationId = `getClaimsMetricsExclusionData-${Date.now()}`;
  try {
    yield put(fetchClaimsMetricsExclusionLoading(true));

    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingStarted(correlationId));
    }

    const response = yield call(
      getClaimsMetricsExclusionDataRequest,
      action?.payload,
      highVolumeTinFlag
    );
    
    if (highVolumeTinFlag === true) {
      // Polling flow expects job submission first
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === 'ACCEPTED' ||
        response?.Data?.Message === 'Job submitted successfully';

      if (jobId && isJobSubmitted) {
        let fullPollingResponse: any = null;
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId,
              {
                onInProgress: () => {},
                onCompleted: (result) => resolve(result),
                onFailed: (errorMessage) => reject(new Error(errorMessage)),
                onStatusUpdate: (response) => { fullPollingResponse = response; }
              },
              '/job-status/result',
              1000
            );
          });
        };

        try {
          const pollingResult = yield call(pollingPromise);
          const transformedData = pollingResult || {};
          yield put(fetchClaimsMetricsExclusionSuccess(transformedData));
        } catch (pollingError: any) {
          yield put(fetchClaimsMetricsExclusionError(true));
        } finally {
          yield put(fetchClaimsMetricsExclusionLoading(false));
          yield put(reimbursementPollingCompleted(correlationId));
        }
      } else {
        yield put(fetchClaimsMetricsExclusionError(true));
        yield put(fetchClaimsMetricsExclusionLoading(false));
        yield put(reimbursementPollingCompleted(correlationId));
      }
    } else {
      // Normal flow - direct API response
      if (response) {
        yield put(fetchClaimsMetricsExclusionSuccess(response));
      } else {
        yield put(fetchClaimsMetricsExclusionError(true));
      }
      yield put(fetchClaimsMetricsExclusionLoading(false));
    }
  } catch (error: any) {
    yield put(fetchClaimsMetricsExclusionError(true));
    yield put(fetchClaimsMetricsExclusionLoading(false));
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      yield put(fetchClaimsMetricsExclusionLoading(false));
      if (highVolumeTinFlag === true) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* getDopClaimsMetricsClaimsData(action: any) {
  // Support dual flow via highVolumeTinFlag
  const highVolumeTinFlag = action?.highVolumeTinFlag;
  const correlationId = `getDopClaimsMetricsClaimsData-${Date.now()}`;
  try {
    yield put(fetchDopClaimsMetricsClaimsLoading(true));
    
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingStarted(correlationId));
    }
    
    const response = yield call(
      getDopClaimsMetricsClaimsDataRequest,
      action?.payload,
      highVolumeTinFlag
    );

    if (highVolumeTinFlag === true) {
      // Polling flow expects job submission first
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === 'ACCEPTED' ||
        response?.Data?.Message === 'Job submitted successfully';

      if (jobId && isJobSubmitted) {
        let fullPollingResponse: any = null;
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId,
              {
                onInProgress: () => {},
                onCompleted: (result) => resolve(result),
                onFailed: (errorMessage) => reject(new Error(errorMessage)),
                onStatusUpdate: (response) => { fullPollingResponse = response; }
              },
              '/job-status/result',
              1000
            );
          });
        };

        try {
          const pollingResult = yield call(pollingPromise);
          const transformedData = pollingResult || [];
          yield put(fetchDopClaimsMetricsClaimsSuccess(transformedData));
        } catch (pollingError: any) {
          yield put(fetchDopClaimsMetricsClaimsError(true));
        } finally {
          yield put(fetchDopClaimsMetricsClaimsLoading(false));
          yield put(reimbursementPollingCompleted(correlationId));
        }
      } else {
        yield put(fetchDopClaimsMetricsClaimsError(true));
        yield put(fetchDopClaimsMetricsClaimsLoading(false));
        yield put(reimbursementPollingCompleted(correlationId));
      }
    } else {
      // Normal flow
      if (response) {
        yield put(fetchDopClaimsMetricsClaimsSuccess(response));
      } else {
        yield put(fetchDopClaimsMetricsClaimsError(true));
      }
      yield put(fetchDopClaimsMetricsClaimsLoading(false));
    }
  } catch (error: any) {
    yield put(fetchDopClaimsMetricsClaimsError(true));
    yield put(fetchDopClaimsMetricsClaimsLoading(false));
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      yield put(fetchDopClaimsMetricsClaimsLoading(false));
      if (highVolumeTinFlag === true) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* getDopClaimsMetricsTatData(action: any) {
  const highVolumeTinFlag = action?.highVolumeTinFlag;
  const correlationId = `getDopClaimsMetricsTatData-${Date.now()}`;
  try {
    yield put(fetchDopClaimsMetricsTatLoading(true));
    
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingStarted(correlationId));
    }
    
    const response = yield call(
      getDopClaimsMetricsTatDataRequest,
      action?.payload,
      highVolumeTinFlag
    );

    if (highVolumeTinFlag === true) {
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === 'ACCEPTED' ||
        response?.Data?.Message === 'Job submitted successfully';

      if (jobId && isJobSubmitted) {
        let fullPollingResponse: any = null;
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId,
              {
                onInProgress: () => {},
                onCompleted: (result) => resolve(result),
                onFailed: (errorMessage) => reject(new Error(errorMessage)),
                onStatusUpdate: (response) => { fullPollingResponse = response; }
              },
              '/job-status/result',
              1000
            );
          });
        };

        try {
          const pollingResult = yield call(pollingPromise);
          const transformedData = pollingResult || [];
          yield put(fetchDopClaimsMetricsTatSuccess(transformedData));
        } catch (pollingError: any) {
          yield put(fetchDopClaimsMetricsTatError(true));
        } finally {
          yield put(fetchDopClaimsMetricsTatLoading(false));
          yield put(reimbursementPollingCompleted(correlationId));
        }
      } else {
        yield put(fetchDopClaimsMetricsTatError(true));
        yield put(fetchDopClaimsMetricsTatLoading(false));
        yield put(reimbursementPollingCompleted(correlationId));
      }
    } else {
      if (response) {
        yield put(fetchDopClaimsMetricsTatSuccess(response));
      } else {
        yield put(fetchDopClaimsMetricsTatError(true));
      }
      yield put(fetchDopClaimsMetricsTatLoading(false));
    }
  } catch (error: any) {
    yield put(fetchDopClaimsMetricsTatError(true));
    yield put(fetchDopClaimsMetricsTatLoading(false));
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      yield put(fetchDopClaimsMetricsTatLoading(false));
      if (highVolumeTinFlag === true) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* claimsProcessedSaga() {
  yield takeLatest(
    CLAIMS_PROCESSED_REQUEST,
    getClaimsProcessedData
  );
  yield takeLatest(
    CLAIMS_METRICS_EXCLUSION_REQUEST,
    getClaimsMetricsExclusionData
  );
  yield takeLatest(
    DOP_CLAIMS_METRICS_CLAIMS_REQUEST,
    getDopClaimsMetricsClaimsData
  );
  yield takeLatest(
    DOP_CLAIMS_METRICS_TAT_REQUEST,
    getDopClaimsMetricsTatData
  );
  yield takeLatest(
    CLAIMS_PROCESSED_MONTHLY_REQUEST,
    getClaimsProcessedMonthlyData
  );
}

export function* getClaimsProcessedMonthlyData(action: any) {
  const highVolumeTinFlag = action?.highVolumeTinFlag;
  const correlationId = `getClaimsProcessedMonthlyData-${Date.now()}`;
  try {
    yield put(fetchClaimsProcessedMonthlyLoading(true));
    yield put(fetchClaimsProcessedMonthlyError(false));

    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingStarted(correlationId));
    }

    const response: any = yield call(
      getClaimsProcessedMonthlyDataRequest,
      action?.payload,
      highVolumeTinFlag
    );

    if (highVolumeTinFlag === true) {
      const jobId = response?.Data?.JobId;
      const isJobSubmitted = response?.Data?.Status === 'ACCEPTED' ||
        response?.Data?.Message === 'Job submitted successfully';

      if (jobId && isJobSubmitted) {
        const pollingPromise = () => {
          return new Promise((resolve, reject) => {
            startJobPolling(
              jobId,
              {
                onInProgress: () => {},
                onCompleted: (result) => resolve(result),
                onFailed: (errorMessage) => reject(new Error(errorMessage)),
                onStatusUpdate: () => {},
              },
              '/job-status/result',
              1000
            );
          });
        };

        try {
          const pollingResult: any = yield call(pollingPromise);
          const lob = action?.payload?.Lob || 'All';
          const normalizedResult = pollingResult ? {
            DataPoint: pollingResult.dataPoint || pollingResult.DataPoint,
            StartDate: pollingResult.startDate || pollingResult.StartDate,
            EndDate: pollingResult.endDate || pollingResult.EndDate,
            Trend: (pollingResult.trend || pollingResult.Trend || []).map((item: any) => ({
              Month: item.month || item.Month,
              PaidCount: item.paidCount ?? item.PaidCount ?? 0,
              NotPaidCount: item.notPaidCount ?? item.NotPaidCount ?? 0,
              TotalProcessed: item.totalProcessed ?? item.TotalProcessed ?? 0,
            })),
          } : pollingResult;
          yield put(fetchClaimsProcessedMonthlySuccess([{ lob, data: normalizedResult }]));
        } catch (pollingError: any) {
          yield put(fetchClaimsProcessedMonthlyError(true));
        } finally {
          yield put(fetchClaimsProcessedMonthlyLoading(false));
          yield put(reimbursementPollingCompleted(correlationId));
        }
      } else {
        yield put(fetchClaimsProcessedMonthlyError(true));
        yield put(fetchClaimsProcessedMonthlyLoading(false));
        yield put(reimbursementPollingCompleted(correlationId));
      }
    } else {
      if (!response) {
        yield put(fetchClaimsProcessedMonthlyError(true));
      } else {
        const lob = action?.payload?.Lob || 'All';
        yield put(fetchClaimsProcessedMonthlySuccess([{ lob, data: response }]));
      }
      yield put(fetchClaimsProcessedMonthlyLoading(false));
    }
  } catch (error: any) {
    yield put(fetchClaimsProcessedMonthlyError(true));
    yield put(fetchClaimsProcessedMonthlyLoading(false));
    if (highVolumeTinFlag === true) {
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } finally {
    if (yield cancelled()) {
      yield put(fetchClaimsProcessedMonthlyLoading(false));
      if (highVolumeTinFlag === true) {
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}
